// Derive a "flight deck profile" from the aircraft type so the cockpit shell
// looks different per manufacturer/family (Airbus vs Boeing vs regional vs GA),
// with the right engine count and colour scheme.

const startsWithAny = (s, prefixes) => prefixes.some((p) => s.startsWith(p));

// Four-engine heavies.
const FOUR_ENGINE = [
  "A388", "A380", "A342", "A343", "A345", "A346", "A340",
  "B741", "B742", "B743", "B744", "B748", "B74",
  "IL96", "AN12", "A124", "AN24",
];
// Common single-engine GA / utility.
const SINGLE_ENGINE = [
  "C72", "C172", "C82", "C182", "C206", "C208", "C20", "C210",
  "P28", "PA28", "SR20", "SR22", "DA40", "C152", "C162", "TBM", "PC12", "PC6",
];

export function engineCount(typeCode) {
  const t = (typeCode || "").toUpperCase();
  if (!t) return 2;
  if (FOUR_ENGINE.some((p) => t.startsWith(p))) return 4;
  if (SINGLE_ENGINE.some((p) => t.startsWith(p))) return 1;
  return 2;
}

// The visual families. Each carries CSS-var colours applied to the shell.
const FAMILIES = {
  airbus: {
    label: "Airbus glass cockpit",
    accent: "#ffb020", // amber FCU
    bezel: "#2b3138",
    glare: "#191d22",
    panelA: "#1b1f24",
    panelB: "#191d22",
    tag: "🇪🇺",
  },
  boeing: {
    label: "Boeing flight deck",
    accent: "#39d98a", // Boeing greenish
    bezel: "#3a352c",
    glare: "#241f18",
    panelA: "#241f18",
    panelB: "#1e1a14",
    tag: "🇺🇸",
  },
  embraer: {
    label: "Embraer E-Jet flight deck",
    accent: "#35d0ff",
    bezel: "#25313a",
    glare: "#141d22",
    panelA: "#161f24",
    panelB: "#131b20",
    tag: "🇧🇷",
  },
  regional: {
    label: "Regional flight deck",
    accent: "#c0d020",
    bezel: "#2f3138",
    glare: "#1a1c20",
    panelA: "#1c1e22",
    panelB: "#191b1f",
    tag: "🛩️",
  },
  ga: {
    label: "General-aviation panel",
    accent: "#e0c040",
    bezel: "#33302a",
    glare: "#1f1c17",
    panelA: "#211e19",
    panelB: "#1b1813",
    tag: "🛩️",
  },
  generic: {
    label: "Glass cockpit",
    accent: "#12e29a",
    bezel: "#2b3138",
    glare: "#191d22",
    panelA: "#1b1f24",
    panelB: "#191d22",
    tag: "✈️",
  },
};

function familyKey(typeCode, manufacturer) {
  const t = (typeCode || "").toUpperCase();
  const m = (manufacturer || "").toLowerCase();

  if (m.includes("airbus") || startsWithAny(t, ["A3", "A2", "A1", "BCS", "A20", "A21", "A22"])) return "airbus";
  if (m.includes("boeing") || startsWithAny(t, ["B7", "B37", "B38", "B75", "B76", "B77", "B78"])) return "boeing";
  if (m.includes("embraer") || startsWithAny(t, ["E17", "E19", "E29", "E75", "E70", "E290", "E195"])) return "embraer";
  if (startsWithAny(t, ["AT", "DH8", "CRJ", "CR", "SF3", "JS", "E12", "SW4", "B190", "D328"])) return "regional";
  if (startsWithAny(t, ["C1", "C2", "PA", "P28", "SR", "DA", "BE", "TBM", "PC12"])) return "ga";
  return "generic";
}

// aircraft = { type? }  (from adsb.lol), details = { aircraft: { typeCode, manufacturer, model } }
export function getCockpitProfile({ typeCode, manufacturer, model } = {}) {
  const family = familyKey(typeCode, manufacturer);
  const base = FAMILIES[family] || FAMILIES.generic;
  // Light general-aviation aircraft use analog "steam gauge" instruments, not a
  // glass EFIS. Airliners / regional jets get the glass PFD/ND/EWD.
  const panelType = family === "ga" ? "analog" : "glass";
  return {
    family,
    panelType,
    engines: engineCount(typeCode),
    typeCode: typeCode || null,
    model: model || null,
    ...base,
  };
}
